// Slice 40 — minimal hash-based router. Eight screens is too few to pull in
// react-router (~30 KB gz of features we don't need). Hash routing keeps the
// SPA index.html-only on the backend — no per-route server config.
//
// API:
//   useRoute()      → { id, params }
//   navigate(id)    → set hash, broadcast
//   <Route id="…">  → renders children when active

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type RouteId =
  | "overview"
  | "licenses"
  | "feedback"
  | "customers"
  | "telemetry"
  | "privacy"
  | "audit"
  | "staff"
  | "settings";

export const ROUTE_IDS: readonly RouteId[] = [
  "overview",
  "licenses",
  "feedback",
  "customers",
  "telemetry",
  "privacy",
  "audit",
  "staff",
  "settings",
];

const DEFAULT_ROUTE: RouteId = "overview";

interface ParsedHash {
  route: RouteId;
  /** Path segments after the route id (e.g. `#/customers/cus_abc` → ["cus_abc"]). */
  segments: string[];
}

function parseHash(hash: string): ParsedHash {
  // Drop leading `#/`, then drop any `?…` query suffix (Slice 47 audit
  // screen takes filter args via the hash — those are not segments).
  const noHash = hash.replace(/^#\/?/, "").split("?")[0] ?? "";
  const parts = noHash.split("/").filter(Boolean);
  const id = parts[0] ?? "";
  if ((ROUTE_IDS as readonly string[]).includes(id)) {
    return { route: id as RouteId, segments: parts.slice(1) };
  }
  return { route: DEFAULT_ROUTE, segments: [] };
}

interface RouterCtx {
  route: RouteId;
  /** Path segments after the route id; empty array on bare `#/customers`. */
  segments: string[];
  /** Optional segments are appended after the route id with `/`. */
  navigate: (id: RouteId, ...segments: string[]) => void;
}

const Ctx = createContext<RouterCtx | null>(null);

export function RouterProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ParsedHash>(() => parseHash(window.location.hash));

  useEffect(() => {
    const onHash = () => setState(parseHash(window.location.hash));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const navigate = useCallback((id: RouteId, ...segments: string[]) => {
    const tail = segments.length ? `/${segments.map(encodeURIComponent).join("/")}` : "";
    window.location.hash = `#/${id}${tail}`;
  }, []);

  const value = useMemo<RouterCtx>(
    () => ({ route: state.route, segments: state.segments, navigate }),
    [state.route, state.segments, navigate],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRouter(): RouterCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useRouter outside <RouterProvider>");
  return c;
}
